import PropTypes from 'prop-types';
import './RightSidebar.css';
import useClickOutside from '../../hooks/useClickOutside';
import { useRef } from 'react';

/**
 * Generic right sidebar container component
 * Provides consistent structure and styling for all right-side panels
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display in the sidebar
 * @param {boolean} props.isOpen - Whether the sidebar is open
 * @param {Function} props.onClose - Callback when close button is clicked
 * @param {string} props.title - Optional title for the sidebar header
 * @param {React.ReactNode} props.headerContent - Optional custom header content (overrides title)
 * @param {boolean} props.showCloseButton - Whether to show the close button (default: true)
 * @param {string} props.className - Additional CSS classes
 */
const RightSidebar = ({
  children,
  isOpen = true,
  onClose = () => {},
  title = '',
  headerContent = null,
  showCloseButton = true,
  className = '',
  closeOnClickOutside = true,
}) => {
  const sidebarRef = useRef(null);
  useClickOutside(sidebarRef, onClose, closeOnClickOutside);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={`right-sidebar ${className}`} ref={sidebarRef}>
      <div className="right-sidebar-header">
        {headerContent || (
          <h2 className="right-sidebar-title">{title}</h2>
        )}
        {showCloseButton && (
          <button
            className="right-sidebar-close"
            onClick={onClose}
            aria-label="Close sidebar"
          >
            ×
          </button>
        )}
      </div>
      <div className="right-sidebar-content">
        {children}
      </div>
    </div>
  );
};

RightSidebar.propTypes = {
  children: PropTypes.node,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  headerContent: PropTypes.node,
  showCloseButton: PropTypes.bool,
  className: PropTypes.string,
  closeOnClickOutside: PropTypes.bool,
};

export default RightSidebar;
